import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { getPosts, deletePost, addPost, updatePost, getPostCategories } from '../services/blogService';
import { getBlogCategories } from '../services/blogCategoryService';
import { hasPermission } from '../services/authService';
import { resolveAssetUrl } from '../utils/resolveUrl';
import API_BASE_URL from '../config';
import toast from 'react-hot-toast';

export default function BlogsPage() {
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [loading, setLoading] = useState(true);

  // Dynamic Page Header Title & Subtitle
  const [pageTitle, setPageTitle] = useState(
    localStorage.getItem('blog_page_title') || 'Zbuloni artikujt më të fundit dhe këshillat nga ekspertët tanë!'
  );
  const [pageSubtitle, setPageSubtitle] = useState(
    localStorage.getItem('blog_page_subtitle') || ''
  );

  const admin = hasPermission('admin.blogs');

  // Modal states
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ id: null, title: '', excerpt: '', content: '', categories: [], image: '', imageFile: null });
  const [newCatInput, setNewCatInput] = useState('');
  const [showPromoSectionInModal, setShowPromoSectionInModal] = useState(true);

  // Promo Banner Settings (for BlogPost.jsx sidebar card)
  const [promoBadge, setPromoBadge] = useState(localStorage.getItem('blog_promo_badge') || 'GLOBE ONLINE');
  const [promoTitle, setPromoTitle] = useState(localStorage.getItem('blog_promo_title') || 'Zbuloni ofertat më të mira në teknologji');
  const [promoDescription, setPromoDescription] = useState(localStorage.getItem('blog_promo_description') || 'Cilësia më e lartë, garanci zyrtare dhe dërgesa të shpejta në të gjithë Shqipërinë.');
  const [promoBtnText, setPromoBtnText] = useState(localStorage.getItem('blog_promo_btn_text') || 'Eksploro Produktet →');
  const [promoBtnLink, setPromoBtnLink] = useState(localStorage.getItem('blog_promo_btn_link') || '/products');

  // Title edit modal for admin
  const [showTitleModal, setShowTitleModal] = useState(false);
  const [tempTitle, setTempTitle] = useState(pageTitle);
  const [tempSubtitle, setTempSubtitle] = useState(pageSubtitle);
  const [savingTitle, setSavingTitle] = useState(false);

  const loadData = async () => {
    try {
      const [p, cats] = await Promise.all([
        getPosts().catch(() => []),
        getBlogCategories().catch(() => [])
      ]);
      setPosts(Array.isArray(p) ? p : []);
      setCategories(Array.isArray(cats) ? cats : []);
    } catch (_) {
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/settings`);
      if (res.ok) {
        const data = await res.json();
        if (data && data.blog_page_title) {
          setPageTitle(data.blog_page_title);
          setTempTitle(data.blog_page_title);
          localStorage.setItem('blog_page_title', data.blog_page_title);
        }
        if (data && data.blog_page_subtitle !== undefined) {
          setPageSubtitle(data.blog_page_subtitle);
          setTempSubtitle(data.blog_page_subtitle);
          localStorage.setItem('blog_page_subtitle', data.blog_page_subtitle);
        }
        if (data && data.blog_promo_badge) {
          setPromoBadge(data.blog_promo_badge);
          localStorage.setItem('blog_promo_badge', data.blog_promo_badge);
        }
        if (data && data.blog_promo_title) {
          setPromoTitle(data.blog_promo_title);
          localStorage.setItem('blog_promo_title', data.blog_promo_title);
        }
        if (data && data.blog_promo_description !== undefined) {
          setPromoDescription(data.blog_promo_description);
          localStorage.setItem('blog_promo_description', data.blog_promo_description);
        }
        if (data && data.blog_promo_btn_text) {
          setPromoBtnText(data.blog_promo_btn_text);
          localStorage.setItem('blog_promo_btn_text', data.blog_promo_btn_text);
        }
        if (data && data.blog_promo_btn_link) {
          setPromoBtnLink(data.blog_promo_btn_link);
          localStorage.setItem('blog_promo_btn_link', data.blog_promo_btn_link);
        }
      }
    } catch (_) {}
  };

  useEffect(() => {
    loadData();
    loadSettings();

    const handleStorage = () => {
      const savedTitle = localStorage.getItem('blog_page_title');
      const savedSubtitle = localStorage.getItem('blog_page_subtitle');
      const b = localStorage.getItem('blog_promo_badge');
      const t = localStorage.getItem('blog_promo_title');
      const d = localStorage.getItem('blog_promo_description');
      const bt = localStorage.getItem('blog_promo_btn_text');
      const bl = localStorage.getItem('blog_promo_btn_link');
      if (savedTitle) {
        setPageTitle(savedTitle);
        setTempTitle(savedTitle);
      }
      if (savedSubtitle !== null) {
        setPageSubtitle(savedSubtitle);
        setTempSubtitle(savedSubtitle);
      }
      if (b) setPromoBadge(b);
      if (t) setPromoTitle(t);
      if (d !== null) setPromoDescription(d);
      if (bt) setPromoBtnText(bt);
      if (bl) setPromoBtnLink(bl);
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Listen for ?category= in URL
  useEffect(() => {
    const queryCat = new URLSearchParams(location.search).get('category');
    if (queryCat) {
      setSelectedCategory(queryCat);
    }
  }, [location.search]);

  const handleSaveTitle = async (e) => {
    if (e) e.preventDefault();
    setSavingTitle(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          blog_page_title: tempTitle,
          blog_page_subtitle: tempSubtitle
        })
      });
      setPageTitle(tempTitle);
      setPageSubtitle(tempSubtitle);
      localStorage.setItem('blog_page_title', tempTitle);
      localStorage.setItem('blog_page_subtitle', tempSubtitle);
      try { window.dispatchEvent(new Event('storage')); } catch (_) {}
      toast.success('Titulli i faqes u përditësua me sukses!');
      setShowTitleModal(false);
    } catch (err) {
      setPageTitle(tempTitle);
      setPageSubtitle(tempSubtitle);
      localStorage.setItem('blog_page_title', tempTitle);
      localStorage.setItem('blog_page_subtitle', tempSubtitle);
      try { window.dispatchEvent(new Event('storage')); } catch (_) {}
      toast.success('Titulli u ruajt lokalisht.');
      setShowTitleModal(false);
    } finally {
      setSavingTitle(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('A jeni të sigurt që dëshironi të fshini këtë artikull?')) return;
    await deletePost(id);
    const updated = await getPosts();
    setPosts(Array.isArray(updated) ? updated : []);
    toast.success('Artikulli u fshi');
  };

  // Flattened known category names from categories tree and posts
  const allKnownCategories = useMemo(() => {
    const list = new Set();
    const addCat = (c) => {
      if (c && c.name) list.add(c.name.trim());
      if (Array.isArray(c.subcategories)) c.subcategories.forEach(addCat);
    };
    categories.forEach(addCat);
    posts.forEach(p => {
      getPostCategories(p).forEach(cat => list.add(cat));
    });
    return Array.from(list).filter(Boolean);
  }, [categories, posts]);

  const openAdd = () => {
    setForm({ id: null, title: '', excerpt: '', content: '', categories: [], image: '', imageFile: null });
    setNewCatInput('');
    setShowPromoSectionInModal(true);
    loadSettings();
    getBlogCategories().then(c => setCategories(c || []));
    setShowForm(true);
  };

  const openEdit = (post) => {
    const postCats = getPostCategories(post);
    setForm({
      id: post.id,
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      categories: postCats,
      image: post.image,
      imageFile: null
    });
    setNewCatInput('');
    setShowPromoSectionInModal(true);
    loadSettings();
    getBlogCategories().then(c => setCategories(c || []));
    setShowForm(true);
  };

  const handleAddCustomCategory = () => {
    const trimmed = newCatInput.trim();
    if (!trimmed) return;
    if (!form.categories.includes(trimmed)) {
      setForm(prev => ({ ...prev, categories: [...prev.categories, trimmed] }));
    }
    setNewCatInput('');
  };

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('title', form.title);
    fd.append('excerpt', form.excerpt);
    fd.append('content', form.content);
    fd.append('category', (form.categories || []).join(', '));
    if (form.imageFile) fd.append('image', form.imageFile);
    if (form.id) await updatePost(form.id, fd);
    else await addPost(fd);

    // Save Promo Banner Settings simultaneously
    try {
      const token = localStorage.getItem('token');
      const payload = {
        blog_page_title: pageTitle,
        blog_page_subtitle: pageSubtitle,
        blog_promo_badge: promoBadge,
        blog_promo_title: promoTitle,
        blog_promo_description: promoDescription,
        blog_promo_btn_text: promoBtnText,
        blog_promo_btn_link: promoBtnLink
      };
      await fetch(`${API_BASE_URL}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      localStorage.setItem('blog_promo_badge', promoBadge);
      localStorage.setItem('blog_promo_title', promoTitle);
      localStorage.setItem('blog_promo_description', promoDescription);
      localStorage.setItem('blog_promo_btn_text', promoBtnText);
      localStorage.setItem('blog_promo_btn_link', promoBtnLink);
      try { window.dispatchEvent(new Event('storage')); } catch (_) {}
    } catch (_) {}

    const updated = await getPosts();
    setPosts(Array.isArray(updated) ? updated : []);
    setShowForm(false);
    toast.success(form.id ? 'Artikulli dhe konfigurimet u përditësuan!' : 'Artikulli dhe konfigurimet u shtuan me sukses!');
  };

  // Group posts by Category (Posts with multiple categories appear under each category group)
  const groupedCategories = useMemo(() => {
    const groups = {};
    const uncategorized = [];

    posts.forEach(post => {
      const postCats = getPostCategories(post);
      if (postCats.length === 0) {
        uncategorized.push(post);
      } else {
        postCats.forEach(catName => {
          if (!groups[catName]) groups[catName] = [];
          if (!groups[catName].some(p => p.id === post.id)) {
            groups[catName].push(post);
          }
        });
      }
    });

    const result = [];
    const knownCatNames = categories.map(c => c.name);

    knownCatNames.forEach(name => {
      if (groups[name] && groups[name].length > 0) {
        result.push({
          name,
          posts: groups[name]
        });
        delete groups[name];
      }
    });

    // Add any remaining categories with posts
    Object.keys(groups).forEach(name => {
      if (groups[name] && groups[name].length > 0) {
        result.push({
          name,
          posts: groups[name]
        });
      }
    });

    // Add uncategorized at the end
    if (uncategorized.length > 0) {
      result.push({
        name: 'Të Tjera',
        posts: uncategorized,
        isUncategorized: true
      });
    }

    return result;
  }, [posts, categories]);

  // Active category groups based on filter tab
  const displayedCategoryGroups = useMemo(() => {
    if (selectedCategory === 'all') {
      return groupedCategories;
    }
    return groupedCategories.filter(g => g.name === selectedCategory);
  }, [groupedCategories, selectedCategory]);

  return (
    <div className="page-container blog-page-container">
      <style>{`
        .blog-page-container {
          padding: 0 11.5% 80px 11.5%;
        }
        .blog-category-pills-bar {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          padding: 14px 0 14px;
          border-bottom: 1px solid #f1f5f9;
          align-items: center;
        }
        .blog-cat-pill-btn {
          padding: 7px 15px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 13px;
          cursor: pointer;
          white-space: nowrap;
          touch-action: manipulation;
          -webkit-tap-highlight-color: transparent;
          transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @media (max-width: 1024px) {
          .blog-page-container {
            padding: 0 5% 60px 5%;
          }
        }
        @media (max-width: 640px) {
          .blog-page-container {
            padding: 0 16px 50px 16px;
          }
          .blog-cat-pill-btn {
            padding: 6px 12px;
            font-size: 12.5px;
          }
        }
      `}</style>

      {/* Top Header Row with Dynamic H1 */}
      <div style={{ marginTop: '32px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ flex: 1, minWidth: '260px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <h1 className="text-3xl sm:text-4xl font-normal" style={{ color: '#0f172a', margin: 0, lineHeight: 1.25, letterSpacing: '-0.5px' }}>
                {pageTitle}
              </h1>
              {admin && (
                <button
                  type="button"
                  onClick={() => {
                    setTempTitle(pageTitle);
                    setTempSubtitle(pageSubtitle);
                    setShowTitleModal(true);
                  }}
                  title="Ndrysho titullin kryesor"
                  style={{
                    background: '#f1f5f9',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    padding: '6px 10px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#007da5',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.2s'
                  }}
                >
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                  Ndrysho Titullin
                </button>
              )}
            </div>
            {pageSubtitle && (
              <p style={{ marginTop: '8px', fontSize: '15px', color: '#64748b', marginBottom: 0 }}>
                {pageSubtitle}
              </p>
            )}
          </div>

          {admin && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <Link
                to="/admin/blogs"
                style={{
                  background: '#f8fafc',
                  color: '#0f172a',
                  border: '1px solid #cbd5e1',
                  padding: '8px 16px',
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: '13.5px',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                Menaxhimi i Blogut
              </Link>
              <button
                type="button"
                onClick={openAdd}
                style={{
                  background: '#007da5',
                  color: '#fff',
                  border: 'none',
                  padding: '8px 18px',
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: '13.5px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: '0 2px 8px rgba(0, 125, 165, 0.25)'
                }}
              >
                + Artikull i Ri
              </button>
            </div>
          )}
        </div>

        {/* Category Navigation Filter Pills (5-6 visible + Më shumë button) */}
        {groupedCategories.length > 1 && (() => {
          const INITIAL_MAX = 4; // 'Të gjitha' + 4 categories = 5 items + 1 'Më shumë' = 6 total
          const hasMore = groupedCategories.length > INITIAL_MAX;
          const isSelectedHidden = selectedCategory !== 'all' && 
            groupedCategories.findIndex(c => c.name === selectedCategory) >= INITIAL_MAX;
          const isExpanded = showAllCategories || isSelectedHidden;
          const visibleCats = isExpanded ? groupedCategories : groupedCategories.slice(0, INITIAL_MAX);

          return (
            <div className="blog-category-pills-bar">
              <button
                type="button"
                className="blog-cat-pill-btn"
                onClick={() => setSelectedCategory('all')}
                style={{
                  border: selectedCategory === 'all' ? '1.5px solid #007da5' : '1.5px solid #e2e8f0',
                  background: selectedCategory === 'all' ? '#007da5' : '#ffffff',
                  color: selectedCategory === 'all' ? '#ffffff' : '#334155',
                  boxShadow: selectedCategory === 'all' ? '0 4px 12px rgba(0, 125, 165, 0.25)' : 'none'
                }}
              >
                Të gjitha ({posts.length})
              </button>
              {visibleCats.map(cat => {
                const isSelected = selectedCategory === cat.name;
                return (
                  <button
                    key={cat.name}
                    type="button"
                    className="blog-cat-pill-btn"
                    onClick={() => setSelectedCategory(cat.name)}
                    style={{
                      border: isSelected ? '1.5px solid #007da5' : '1.5px solid #e2e8f0',
                      background: isSelected ? '#007da5' : '#ffffff',
                      color: isSelected ? '#ffffff' : '#334155',
                      boxShadow: isSelected ? '0 4px 12px rgba(0, 125, 165, 0.25)' : 'none'
                    }}
                  >
                    {cat.name} ({cat.posts.length})
                  </button>
                );
              })}

              {hasMore && (
                <button
                  type="button"
                  className="blog-cat-pill-btn"
                  onClick={() => setShowAllCategories(prev => !prev)}
                  style={{
                    border: '1.5px dashed #007da5',
                    background: isExpanded ? '#e0f2fe' : '#f0f9ff',
                    color: '#007da5',
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5
                  }}
                >
                  {isExpanded ? 'Më pak ▲' : `+ Më shumë (${groupedCategories.length - INITIAL_MAX})`}
                </button>
              )}
            </div>
          );
        })()}
      </div>

      {/* Categorized Blog Sections */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b', fontSize: '16px' }}>
          Po ngarkohen artikujt…
        </div>
      ) : displayedCategoryGroups.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 20px', background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', marginTop: '30px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#334155', marginBottom: 8 }}>Nuk u gjet asnjë artikull</h3>
          <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>Nuk ka artikuj të postuar për këtë kategori.</p>
        </div>
      ) : (
        displayedCategoryGroups.map((group) => (
          <section key={group.name} className="blog-category-section" style={{ marginTop: '42px', marginBottom: '52px' }}>
            {/* Category H1 Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '22px', borderBottom: '2px solid #e2e8f0', paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '4px', height: '24px', background: '#007da5', borderRadius: '2px' }} />
                <h1 style={{ fontSize: '1.65rem', fontWeight: 600, color: '#0f172a', margin: 0, letterSpacing: '-0.4px' }}>
                  {group.name}
                </h1>
                <span style={{ fontSize: '12.5px', fontWeight: 600, color: '#64748b', background: '#f1f5f9', padding: '3px 10px', borderRadius: '12px' }}>
                  {group.posts.length} {group.posts.length === 1 ? 'artikull' : 'artikuj'}
                </span>
              </div>
            </div>

            {/* Cards Grid for this category */}
            <div className="cards-wrapper">
              {group.posts.map((post) => {
                const imgUrl = post.image ? resolveAssetUrl(post.image) : 'https://picsum.photos/seed/blog' + post.id + '/800/600';
                const postCats = getPostCategories(post);
                return (
                  <div key={post.id} style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
                    {admin && (
                      <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 10, display: 'flex', gap: 6 }}>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); openEdit(post); }}
                          style={{
                            background: '#ffffff',
                            color: '#0f172a',
                            border: '1px solid #cbd5e1',
                            padding: '4px 10px',
                            borderRadius: 6,
                            fontSize: 12,
                            cursor: 'pointer',
                            fontWeight: 600,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); handleDelete(post.id); }}
                          style={{
                            background: '#fee2e2',
                            color: '#dc2626',
                            border: '1px solid #fca5a5',
                            padding: '4px 10px',
                            borderRadius: 6,
                            fontSize: 12,
                            cursor: 'pointer',
                            fontWeight: 600,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                    <Link className="mac-blog-card" to={`/blog/${post.id}`}>
                      <div className="mac-blog-img-wrap">
                        <img src={imgUrl} alt={post.title} loading="lazy" />
                      </div>
                      <div className="mac-blog-content">
                        <div className="mac-blog-meta" style={{ flexWrap: 'wrap', gap: 6 }}>
                          {postCats.map(cat => (
                            <span key={cat} className="mac-blog-tag">{cat}</span>
                          ))}
                          {post.createdAt && <span className="mac-blog-date">{new Date(post.createdAt).toLocaleDateString()}</span>}
                        </div>
                        <h3 className="mac-blog-title">{post.title}</h3>
                        <p className="mac-blog-excerpt">
                          {post.excerpt || (post.content ? post.content.replace(/<[^>]+>/g, '').slice(0, 110) + '…' : '')}
                        </p>
                        <div className="mac-blog-footer">
                          <span>Lexo artikullin</span>
                          <span>→</span>
                        </div>
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}

      {/* Edit Header Title Modal */}
      {showTitleModal && typeof document !== 'undefined' && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', zIndex: 999999, padding: '20px', boxSizing: 'border-box' }}>
          <div style={{ width: '100%', maxWidth: 600, background: '#fff', padding: 24, borderRadius: 12, position: 'relative', boxShadow: '0 20px 40px rgba(0,0,0,0.25)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Ndrysho Titullin e Faqes së Blogut</h3>
              <button
                type="button"
                onClick={() => setShowTitleModal(false)}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#64748b' }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSaveTitle} style={{ display: 'grid', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                  H1 Page Title (Titulli Kryesor)
                </label>
                <textarea
                  rows={3}
                  value={tempTitle}
                  onChange={(e) => setTempTitle(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                  Nëntitulli (Opsional)
                </label>
                <input
                  type="text"
                  value={tempSubtitle}
                  onChange={(e) => setTempSubtitle(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => setShowTitleModal(false)}
                  style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
                >
                  Anulo
                </button>
                <button
                  type="submit"
                  disabled={savingTitle}
                  style={{ padding: '8px 20px', background: '#007da5', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
                >
                  {savingTitle ? 'Duke ruajtur...' : 'Ruaj'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Add / Edit Blog Post Modal */}
      {showForm && typeof document !== 'undefined' && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', zIndex: 999999, padding: '20px', boxSizing: 'border-box' }}>
          <div style={{ width: '100%', maxWidth: 700, maxHeight: '90vh', overflowY: 'auto', overflowX: 'hidden', background: '#fff', padding: 24, borderRadius: 12, position: 'relative', boxShadow: '0 20px 40px rgba(0,0,0,0.25)' }}>
            <div style={{ position: 'sticky', top: 0, background: '#fff', paddingBottom: 12, marginBottom: 16, zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{form.id ? 'Edito Artikullin' : 'Artikull i Ri'}</h3>
              <button aria-label="Close modal" onClick={() => setShowForm(false)}
                style={{ width: 32, height: 32, borderRadius: 16, border: '1px solid #ddd', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}>
                ×
              </button>
            </div>
            <form onSubmit={submit} style={{ display: 'grid', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Titulli</label>
                <input placeholder='Titulli i artikullit' value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} required />
              </div>

              {/* Multi-Category Selection */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                  Kategoritë (Zgjidhni një ose disa kategori)
                </label>
                
                {/* Active Selected Category Badges */}
                {form.categories && form.categories.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                    {form.categories.map(cat => (
                      <span
                        key={cat}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          background: '#007da5',
                          color: '#ffffff',
                          padding: '6px 12px',
                          borderRadius: 8,
                          fontSize: 13,
                          fontWeight: 600,
                          boxShadow: '0 1px 3px rgba(0, 125, 165, 0.2)'
                        }}
                      >
                        <span>{cat}</span>
                        <button
                          type="button"
                          onClick={() => setForm(prev => ({ ...prev, categories: prev.categories.filter(c => c !== cat) }))}
                          style={{
                            background: 'rgba(255,255,255,0.25)',
                            border: 'none',
                            borderRadius: '50%',
                            width: 20,
                            height: 20,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: 14,
                            lineHeight: 1
                          }}
                          title={`Hiq ${cat}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 12.5, color: '#94a3b8', marginBottom: 8, fontStyle: 'italic' }}>
                    Asnjë kategori e zgjedhur (artikulli do të vendoset te "Të Tjera").
                  </div>
                )}

                {/* Available Categories Toggle Buttons */}
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    padding: 12,
                    background: '#f8fafc',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: 10,
                    maxHeight: 180,
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch'
                  }}
                >
                  {allKnownCategories.map(catName => {
                    const isChecked = form.categories?.includes(catName);
                    return (
                      <button
                        key={catName}
                        type="button"
                        onClick={() => {
                          setForm(prev => {
                            const current = prev.categories || [];
                            const next = isChecked ? current.filter(c => c !== catName) : [...current, catName];
                            return { ...prev, categories: next };
                          });
                        }}
                        style={{
                          padding: '7px 14px',
                          borderRadius: 8,
                          border: isChecked ? '1.5px solid #007da5' : '1px solid #cbd5e1',
                          background: isChecked ? '#e0f2fe' : '#ffffff',
                          color: isChecked ? '#007da5' : '#334155',
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          minHeight: 36,
                          touchAction: 'manipulation',
                          userSelect: 'none',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <span style={{ fontSize: 14, fontWeight: 700 }}>{isChecked ? '✓' : '+'}</span>
                        <span>{catName}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Add Custom Category Input */}
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    placeholder="Shto një kategori të re..."
                    value={newCatInput}
                    onChange={(e) => setNewCatInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustomCategory();
                      }
                    }}
                    style={{ flex: '1 1 180px', padding: '8px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 13, minHeight: 38, boxSizing: 'border-box' }}
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomCategory}
                    style={{ padding: '8px 16px', minHeight: 38, background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}
                  >
                    + Shto Kategori
                  </button>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Imazhi kryesor</label>
                <input type='file' accept='image/*' onChange={(e) => setForm({ ...form, imageFile: e.target.files?.[0] || null })} style={{ width: '100%', padding: '8px 0' }} />
                {form.image && !form.imageFile ? <div style={{ marginTop: 8 }}><img src={resolveAssetUrl(form.image)} alt='blog' style={{ maxHeight: 120, borderRadius: 6 }} /></div> : null}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Përmbledhja (Excerpt)</label>
                <input placeholder='Përmbledhje e shkurtër...' value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Përmbajtja (Content)</label>
                <textarea placeholder='Shkruani përmbajtjen e artikullit...' rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
              </div>

              {/* Sidebar Promo Card Editor */}
              <div style={{ border: '1.5px solid #cbd5e1', borderRadius: 10, overflow: 'hidden', background: '#f8fafc', marginTop: 4 }}>
                <div
                  onClick={() => setShowPromoSectionInModal(s => !s)}
                  style={{
                    padding: '10px 16px',
                    background: '#f1f5f9',
                    borderBottom: showPromoSectionInModal ? '1px solid #cbd5e1' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>📢</span>
                    <span style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>
                      Edito Kartën Reklamuese të Sidebar-it (Promo Banner)
                    </span>
                  </div>
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: '#007da5' }}>
                    {showPromoSectionInModal ? 'Fshih ▲' : 'Shfaq & Edito ▼'}
                  </span>
                </div>

                {showPromoSectionInModal && (
                  <div style={{ padding: 14, background: '#ffffff', display: 'grid', gap: 12 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Badge i sipërm</label>
                        <input
                          type="text"
                          value={promoBadge}
                          onChange={(e) => setPromoBadge(e.target.value)}
                          placeholder="psh. GLOBE ONLINE"
                          style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Titulli i Kartës</label>
                        <input
                          type="text"
                          value={promoTitle}
                          onChange={(e) => setPromoTitle(e.target.value)}
                          placeholder="psh. Zbuloni ofertat më të mira"
                          style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Përshkrimi</label>
                      <textarea
                        rows={2}
                        value={promoDescription}
                        onChange={(e) => setPromoDescription(e.target.value)}
                        placeholder="Përshkrimi i kartës në sidebar..."
                        style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Teksti i Butonit</label>
                        <input
                          type="text"
                          value={promoBtnText}
                          onChange={(e) => setPromoBtnText(e.target.value)}
                          placeholder="psh. Eksploro Produktet →"
                          style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Linku i Butonit</label>
                        <input
                          type="text"
                          value={promoBtnLink}
                          onChange={(e) => setPromoBtnLink(e.target.value)}
                          placeholder="psh. /products"
                          style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                <button type='button' onClick={() => setShowForm(false)} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Anulo</button>
                <button type='submit' style={{ padding: '8px 20px', background: '#007da5', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Ruaj Artikullin & Reklamën</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
